// Shared presentation contract for the browser preview and the server PDF.
// This module never reads current payroll assignments or recalculates payroll.
const DEFAULT_STYLE = {
  logo_position: 'left', company_info_format: 'detailed',
  colors: { primary: '#2c5282', secondary: '#e2e8f0', accent: '#1890ff' },
  fonts: { family: 'Helvetica', sizes: { title: 20, header: 14, body: 12, small: 10 } },
};
const DEFAULT_PRINT = {
  page_size: 'A4', orientation: 'portrait', margin: '12mm', show_logo: true,
  show_company_details: true, show_employee_photo: false, show_signature_area: true,
  show_stamp_area: true, show_iuts_breakdown: false, show_calculation_details: false,
};

const ELEMENT_CODE_ALIASES = Object.freeze({
  IRPP: 'IUTS', IUTS_NET: 'IUTS', AUTRES_IND: 'AUTRES_PRIMES', IND_CAISSE: 'AUTRES_PRIMES',
  IND_RESP: 'IND_FONC', AVT_NAT: 'AVANTAGES_NATURE', PRET_EMP: 'PRETS', AVANCE_SAL: 'AVANCES',
});
const CANONICAL_ELEMENT_CODES = new Set(Object.values(ELEMENT_CODE_ALIASES));
function canonicalPayslipElementCode(code = '') {
  const name = String(code);
  const upper = name.toUpperCase();
  if (Object.prototype.hasOwnProperty.call(ELEMENT_CODE_ALIASES, upper)) return ELEMENT_CODE_ALIASES[upper];
  return CANONICAL_ELEMENT_CODES.has(upper) ? upper : name;
}
/**
 * @template T
 * @param {Record<string, T>} [config={}]
 * @returns {Record<string, T>}
 */
function normalizePayslipElementAliases(config = {}) {
  const entries = Object.entries(object(config)).filter(([code]) => !['__proto__', 'constructor', 'prototype'].includes(code));
  /** @type {Record<string, T>} */
  const normalized = {};
  // An explicit canonical entry keeps the established historical rendering.
  // Otherwise the first configured alias supplies the single group control.
  entries.forEach(([code, value]) => {
    if (canonicalPayslipElementCode(code) === code) normalized[code] = value;
  });
  entries.forEach(([code, value]) => {
    const canonical = canonicalPayslipElementCode(code);
    if (!Object.prototype.hasOwnProperty.call(normalized, canonical)) normalized[canonical] = value;
  });
  return normalized;
}
function configuredElement(config, code) {
  const canonical = canonicalPayslipElementCode(code);
  if (Object.prototype.hasOwnProperty.call(config, canonical)) return config[canonical];
  const upper = canonical.toUpperCase();
  if (Object.prototype.hasOwnProperty.call(config, upper)) return config[upper];
  const matching = Object.keys(config).find(key => key.toUpperCase() === upper);
  return matching ? config[matching] : {};
}

function object(value) {
  if (typeof value === 'string') {
    try { value = JSON.parse(value); } catch { return {}; }
  }
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}
function number(value, fallback = 0) {
  if (value === null || value === undefined || value === '') return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}
function firstNumber(values, fallback = 0) {
  for (const value of values) {
    if (value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value))) return Number(value);
  }
  return fallback;
}
function assertStoredNumber(value) {
  if (value !== null && value !== undefined && value !== '' &&
    (!['number', 'string'].includes(typeof value) || (typeof value === 'string' && !value.trim()) || !Number.isFinite(Number(value)))) {
    throw Object.assign(new Error('Un montant historique du bulletin est invalide'), { statusCode: 422, code: 'INVALID_PAYSLIP_SNAPSHOT' });
  }
}
function escapeHTML(value) {
  return String(value ?? '').replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));
}
function color(value, fallback) {
  return /^#[0-9a-f]{3}(?:[0-9a-f]{3})?$/i.test(value || '') ? value : fallback;
}

function normalizePayslipTemplateConfig(value = {}) {
  const template = object(value);
  const type = ['professional', 'compact', 'detailed', 'custom'].includes(template.template_type) ? template.template_type : 'professional';
  const style = object(template.style_config);
  const fonts = object(style.fonts);
  const sizes = object(fonts.sizes);
  const print = object(template.print_config);
  const defaults = type === 'compact' ? { ...DEFAULT_PRINT, margin: '8mm', show_signature_area: false, show_stamp_area: false } :
    type === 'detailed' ? { ...DEFAULT_PRINT, show_iuts_breakdown: true, show_calculation_details: true } : DEFAULT_PRINT;
  const normalizedPrint = { ...defaults };
  Object.keys(defaults).forEach(key => {
    if (typeof defaults[key] === 'boolean' && typeof print[key] === 'boolean') normalizedPrint[key] = print[key];
  });
  normalizedPrint.page_size = ['A4', 'Letter'].includes(print.page_size || print.format) ? (print.page_size || print.format) : 'A4';
  normalizedPrint.orientation = print.orientation === 'landscape' ? 'landscape' : 'portrait';
  const margin = print.margin || print.margins;
  const margins = typeof margin === 'string' ? margin.trim().split(/\s+/) : [];
  if (margins.length >= 1 && margins.length <= 4 && margins.every(part => /^\d+(?:\.\d+)?mm$/.test(part) && number(part.replace('mm', '')) <= 30)) normalizedPrint.margin = margins.join(' ');
  const normalizedSizes = { ...DEFAULT_STYLE.fonts.sizes };
  Object.keys(normalizedSizes).forEach(key => {
    const fontSize = number(sizes[key], normalizedSizes[key]);
    const limits = { title: [14, 30], header: [10, 20], body: [8, 16], small: [6, 12] }[key];
    normalizedSizes[key] = Math.max(limits[0], Math.min(limits[1], fontSize));
  });
  // Core PDF fonts are embedded by the renderer without a network request.
  const family = ['Inter', 'Arial', 'Helvetica', 'Times New Roman', 'Times-Roman', 'Courier'].includes(fonts.family) ? fonts.family : 'Helvetica';
  const normalizedElements = {};
  Object.entries(object(template.elements_config)).forEach(([code, entry]) => {
    if (!/^[A-Za-z0-9_.-]{1,80}$/.test(code) || ['__proto__', 'constructor', 'prototype'].includes(code)) return;
    const config = object(entry);
    normalizedElements[code] = {
      visible: config.visible !== false,
      display_order: number(config.display_order, 1000),
      ...(typeof config.label_override === 'string' ? { label_override: config.label_override.slice(0, 255) } : {}),
      format: ['currency', 'percentage', 'text'].includes(config.format) ? config.format : 'currency',
    };
  });
  return {
    ...template, template_type: type, elements_config: normalizePayslipElementAliases(normalizedElements),
    style_config: {
      logo_position: ['left', 'center', 'right'].includes(style.logo_position) ? style.logo_position : 'left',
      company_info_format: style.company_info_format === 'compact' ? 'compact' : 'detailed',
      colors: Object.fromEntries(Object.entries(DEFAULT_STYLE.colors).map(([key, fallback]) => [key, color(object(style.colors)[key], fallback)])),
      fonts: { family, sizes: normalizedSizes },
    },
    print_config: normalizedPrint,
  };
}

function safeAssetUrl(value) {
  if (typeof value !== 'string') return '';
  const inline = /^data:image\/(?:png|jpeg);base64,([a-z0-9+/=]+)$/i.exec(value);
  if (inline) {
    const decodedSize = inline[1].length * 3 / 4 - (inline[1].endsWith('==') ? 2 : inline[1].endsWith('=') ? 1 : 0);
    return decodedSize <= 2 * 1024 * 1024 ? value : '';
  }
  const normalized = value.startsWith('uploads/') ? `/${value}` : value;
  if (!/^\/uploads\/[A-Za-z0-9_./% -]+\.(?:png|jpg|jpeg)$/i.test(normalized)) return '';
  try {
    const decoded = decodeURIComponent(normalized);
    if (decoded.split('/').some(part => part === '..' || part === '.') || decoded.includes('\\')) return '';
    return normalized;
  } catch { return ''; }
}
function formatMoney(value) {
  return number(value).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).replace(/[\u00a0\u202f]/g, ' ');
}
function formatDate(value) {
  if (!value) return '';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toLocaleDateString('fr-FR', { timeZone: 'UTC' });
}
function seniority(hireDate, endDate) {
  const start = new Date(hireDate);
  const end = new Date(endDate);
  if (!hireDate || !endDate || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return '';
  let years = end.getUTCFullYear() - start.getUTCFullYear();
  let months = end.getUTCMonth() - start.getUTCMonth();
  if (end.getUTCDate() < start.getUTCDate()) months--;
  if (months < 0) { years--; months += 12; }
  return [years ? `${years} an${years > 1 ? 's' : ''}` : '', months ? `${months} mois` : ''].filter(Boolean).join(' ') || 'Moins d’un mois';
}

const GAIN_FIELDS = [
  ['basicSalary', 'SAL_BASE', 'Salaire de base'], ['seniorityBonus', 'PRIME_ANC', 'Prime d’ancienneté'],
  ['additionalSalary', 'SURSALAIRE', 'Sursalaire'], ['housingAllowance', 'IND_LOG', 'Indemnité de logement'],
  ['transportAllowance', 'IND_TRANS', 'Indemnité de transport'], ['responsibilityAllowance', 'IND_FONC', 'Indemnité de fonction', 'IND_RESP'],
  ['otherAllowances', 'AUTRES_PRIMES', 'Autres indemnités', 'AUTRES_IND', 'IND_CAISSE'],
  ['naturalBenefits', 'AVANTAGES_NATURE', 'Avantages en nature', 'AVT_NAT'],
  ['overtime15', 'HS_15', 'Heures supplémentaires 15 %'], ['overtime35', 'HS_35', 'Heures supplémentaires 35 %'],
];
const DEDUCTION_FIELDS = [
  ['loans', 'PRETS', 'Remboursement de prêts', 'PRET_EMP'],
  ['advances', 'AVANCES', 'Remboursement d’avances', 'AVANCE_SAL'],
  ['otherDeductions', 'AUTRES_RET', 'Autres retenues'],
];
function storedRows(data) {
  const details = object(data.calculation_details);
  const calculations = object(data.calculations);
  const earnings = object(data.earnings || details.earnings);
  const deductions = object(data.deductions || details.deductions);
  const explicit = calculations.payroll_elements || data.payroll_elements || details.payroll_elements;
  if (Array.isArray(explicit) && explicit.length) return explicit.map((entry, index) => ({
    ...entry, code: String(entry.code || `ITEM_${index}`),
    name: String(entry.name || entry.display_name || 'Rubrique'),
    type: entry.type === 'deduction' ? 'deduction' : 'gain', amount: number(entry.amount),
    display_order: number(entry.display_order, index), aliases: [],
  }));
  const rows = [];
  GAIN_FIELDS.forEach(([field, code, name, ...aliases], index) => {
    if (number(earnings[field]) !== 0) rows.push({ code, name, aliases, type: 'gain', amount: number(earnings[field]), display_order: index + 1, base_amount: null, rate: null });
  });
  // Historic overtime with no split is retained; the stored amount is never recomputed from hours.
  if (!rows.some(row => row.code === 'HS_15' || row.code === 'HS_35') && number(earnings.overtimeAmount) !== 0) {
    rows.push({ code: 'HS', name: 'Heures supplémentaires', aliases: [], type: 'gain', amount: number(earnings.overtimeAmount), display_order: 10 });
  }
  [['cnss', 'CNSS_EMP', 'CNSS salarié', 'employeeAmount', 'base', 'employeeRate'],
    ['iuts', 'IUTS', 'IUTS', 'iutsNetToPay', 'taxableIncome', null],
    ['fsp', 'FSP', 'Fonds de solidarité', 'amount', 'baseAmount', 'rate']].forEach(([field, code, name, amountKey, baseKey, rateKey], index) => {
    const stored = object(deductions[field]);
    if (number(stored[amountKey]) !== 0) rows.push({ code, name, aliases: code === 'IUTS' ? ['IRPP'] : [], type: 'deduction', amount: number(stored[amountKey]),
      base_amount: stored[baseKey], rate: rateKey ? stored[rateKey] : null, display_order: 20 + index });
  });
  DEDUCTION_FIELDS.forEach(([field, code, name, ...aliases], index) => {
    if (number(deductions[field]) !== 0) rows.push({ code, name, aliases, type: 'deduction', amount: number(deductions[field]), display_order: 30 + index });
  });
  // Custom rubrics are stored by the calculator as item_<code>. Only these
  // historical amounts are read; a current assignment must never alter them.
  [[earnings, 'gain'], [deductions, 'deduction']].forEach(([values, type]) => {
    Object.entries(values).forEach(([key, amount], index) => {
      if (/^item_[a-z0-9_.-]+$/i.test(key) && number(amount) !== 0) {
        const code = key.slice(5).toUpperCase();
        if (!rows.some(row => row.code === code && row.type === type)) {
          rows.push({ code, name: code, aliases: [], type, amount: number(amount), display_order: 100 + index });
        }
      }
    });
  });
  return rows;
}

function createPayslipRenderModel(data = {}, templateValue = {}, settings = {}) {
  const template = normalizePayslipTemplateConfig(templateValue);
  const details = object(data.calculation_details);
  const calculations = object(data.calculations);
  const earnings = object(data.earnings || details.earnings);
  const deductions = object(data.deductions || details.deductions);
  const final = object(data.finalAmounts);
  const totals = object(calculations.totals);
  [final.grossSalary, final.netSalary, final.netToPay, final.totalDeductions, final.taxableBase, final.employerCharges,
    data.grossSalary, data.gross_salary, data.netSalary, data.net_salary, data.totalDeductions, data.total_deductions,
    calculations.gross_salary, calculations.net_salary, calculations.total_deductions, calculations.taxable_base,
    totals.total_gains, totals.total_deductions, totals.net_to_pay, earnings.grossSalary, earnings.totalRemuneration,
    deductions.totalDeductions, details.netSalary, details.employerCosts?.totalEmployerCost].forEach(assertStoredNumber);
  GAIN_FIELDS.forEach(([field]) => assertStoredNumber(earnings[field]));
  DEDUCTION_FIELDS.forEach(([field]) => assertStoredNumber(deductions[field]));
  [earnings, deductions].forEach(values => Object.entries(values).forEach(([key, amount]) => {
    if (/^item_/i.test(key)) assertStoredNumber(amount);
  }));
  ['cnss', 'iuts', 'fsp'].forEach(key => {
    const entry = object(deductions[key]);
    ['employeeAmount', 'employerAmount', 'iutsNetToPay', 'amount', 'base', 'baseAmount', 'taxableIncome', 'employeeRate', 'rate'].forEach(field => assertStoredNumber(entry[field]));
  });
  const explicitRows = calculations.payroll_elements || data.payroll_elements || details.payroll_elements;
  if (Array.isArray(explicitRows)) explicitRows.forEach(row => [row.amount, row.base_amount, row.rate].forEach(assertStoredNumber));
  const allRows = storedRows(data);
  const rows = allRows.map(row => {
    const config = configuredElement(template.elements_config, row.code);
    return { ...row, name: config.label_override || row.name, visible: config.visible !== false,
      display_order: firstNumber([config.display_order, row.display_order]),
      amount_text: formatMoney(row.amount), base_text: row.base_amount == null ? '' : formatMoney(row.base_amount),
      rate_text: row.rate == null ? '' : `${formatMoney(row.rate)} %` };
  }).filter(row => row.visible).sort((a, b) => a.display_order - b.display_order);
  const gainsTotal = firstNumber([totals.total_gains, earnings.totalRemuneration], allRows.filter(row => row.type === 'gain').reduce((sum, row) => sum + row.amount, 0));
  const gross = firstNumber([final.grossSalary, data.grossSalary, data.gross_salary, calculations.gross_salary, earnings.grossSalary], gainsTotal);
  const deductionTotal = firstNumber([final.totalDeductions, data.totalDeductions, data.total_deductions, calculations.total_deductions, totals.total_deductions, deductions.totalDeductions], allRows.filter(row => row.type === 'deduction').reduce((sum, row) => sum + row.amount, 0));
  const net = firstNumber([final.netToPay, final.netSalary, data.netSalary, data.net_salary, calculations.net_salary, totals.net_to_pay, details.netSalary]);
  const taxable = firstNumber([final.taxableBase, calculations.taxable_base, deductions.iuts?.taxableIncome]);
  const company = { ...object(data.company), ...object(settings.company) };
  const employee = object(data.employee);
  const period = object(data.period);
  const storedCurrency = object(data.legislation).currency_code !== undefined
    ? object(data.legislation).currency_code : object(details.legislation).currency_code;
  const currencySource = storedCurrency !== undefined ? storedCurrency
    : settings.currency_code !== undefined ? settings.currency_code : company.currency_code;
  const config = template.elements_config;
  const summary = [
    ['TOTAL_GAINS', 'Total gains', gainsTotal], ['GROSS_SALARY', 'Salaire brut', gross],
    ['TAXABLE_BASE', 'Base imposable', taxable], ['TOTAL_DEDUCTIONS', 'Total retenues', deductionTotal],
    ['NET_SALARY', 'Net à payer', net],
  ].map(([code, label, amount]) => ({ code, label: config[code]?.label_override || label, amount, amount_text: formatMoney(amount) }));
  const employer = firstNumber([final.employerCharges, calculations.employer_charges, data.employer_charges, details.employerCosts?.totalEmployerCost]);
  const sequence = object(details.calculation_sequence);
  const stepValues = [
    ['Rémunération totale', sequence.totalRemuneration], ['CNSS salariale', sequence.cnssEmployeeAmount],
    ['Salaire brut enregistré', sequence.grossSalary], ['Exonérations', sequence.exemptions?.totalExemptions],
    ['Abattement', typeof sequence.allowanceDeduction === 'object' ? sequence.allowanceDeduction?.amount : sequence.allowanceDeduction],
    ['Base imposable nette', sequence.netTaxableIncome], ['IUTS avant réduction', sequence.iutsZeroCharge],
    ['IUTS dû', sequence.iutsNetToPay], ['CNSS employeur', details.employerCosts?.cnssEmployer],
  ].filter(([, value]) => value !== undefined && value !== null && value !== '');
  stepValues.forEach(([, value]) => assertStoredNumber(value));
  const iuts = object(deductions.iuts);
  const taxBreakdown = calculations.iuts_breakdown?.tax_brackets || iuts.taxDetails || [];
  return {
    template, company, employee, period, rows, allRows, summary, gross, net, totalGains: gainsTotal, totalDeductions: deductionTotal, taxable, isPreview: settings.preview === true,
    hiddenCount: allRows.length - rows.length,
    employerCharges: employer, calculationSteps: stepValues.map(([label, value]) => ({ label, amount_text: formatMoney(value) })),
    calculationDate: formatDate(data.calculatedAt || details.calculatedAt || data.updated_at || data.created_at),
    companyName: company.name || settings.name || 'Entreprise',
    employeeName: employee.full_name || `${employee.first_name || ''} ${employee.last_name || ''}`.trim(),
    periodName: period.name || period.period_name || '',
    seniority: seniority(employee.hire_date, period.end_date),
    currency: typeof currencySource === 'string' && /^[A-Z]{3}$/.test(currencySource) ? currencySource : '',
    logo: safeAssetUrl(settings.logo || settings.logo_url || company.logo || company.light_logo),
    photo: safeAssetUrl(employee.photo_url),
    legalDetails: [company.employer_number ? `N° employeur : ${company.employer_number}` : '',
      company.tax_number ? `N° fiscal : ${company.tax_number}` : '', company.rccm_number ? `RCCM : ${company.rccm_number}` : '',
      company.registration_number ? `Immatriculation : ${company.registration_number}` : '', company.legal_status || '',
      company.capital_social ? `Capital : ${company.capital_social}` : ''].filter(Boolean).join(' · '),
    taxRows: Array.isArray(taxBreakdown) ? taxBreakdown.map(row => ({
      label: row.bracket || row.label || '', rate: row.rate, base: row.base ?? row.taxableAmount, amount: row.amount ?? row.taxAmount,
    })) : [],
    fields: [
      ['Matricule', employee.employee_number], ['Nom et prénom', employee.full_name || `${employee.first_name || ''} ${employee.last_name || ''}`.trim()],
      ['Date d’embauche', formatDate(employee.hire_date)], ['N° sécurité sociale', employee.social_security_number],
      ['Catégorie', employee.professional_category || employee.category], ['Ancienneté', seniority(employee.hire_date, period.end_date)],
      ['Emploi', employee.position_title || data.position?.title], ['Département', employee.department_name || data.department?.name],
      ['Mode de paiement', employee.payment_mode], ['Banque', employee.bank_name], ['N° compte', employee.bank_account],
      ['Charges familiales', employee.dependents_count ?? ''],
    ].filter(([, value]) => value !== '' && value !== null && value !== undefined),
  };
}

function renderPayslipHTML(model) {
  const { template, company, period } = model;
  const style = template.style_config;
  const print = template.print_config;
  const standard = isStandardPayslipLayout(template);
  const summary = standard ? standardPayslipSummary(model) : model.summary;
  const net = model.summary.find(total => total.code === 'NET_SALARY');
  const cssFont = ['Times New Roman', 'Times-Roman'].includes(style.fonts.family) ? '"Times New Roman",serif' :
    style.fonts.family === 'Courier' ? '"Courier New",monospace' : 'Arial,Helvetica,sans-serif';
  const details = print.show_company_details ? `<div class="company-details">${escapeHTML(company.address || '')}${style.company_info_format === 'detailed' ? `<br>${escapeHTML([company.phone, company.email].filter(Boolean).join(' · '))}${model.legalDetails ? `<br>${escapeHTML(model.legalDetails)}` : ''}` : ''}</div>` : '';
  const logo = print.show_logo && model.logo ? `<img class="logo" src="${escapeHTML(model.logo)}" alt="Logo de l’entreprise">` : '';
  const photo = print.show_employee_photo && model.photo ? `<img class="photo" src="${escapeHTML(model.photo)}" alt="Photo du salarié">` : '';
  const rowHTML = row => `<tr data-code="${escapeHTML(row.code)}"><td>${escapeHTML(row.name)}</td><td class="amount">${escapeHTML(row.rate_text)}</td><td class="amount">${escapeHTML(row.base_text)}</td><td class="amount">${row.type === 'deduction' ? row.amount_text : ''}</td><td class="amount">${row.type === 'gain' ? row.amount_text : ''}</td></tr>`;
  const tax = print.show_iuts_breakdown && model.taxRows.length ? `<section><h2>Détail IUTS</h2><table><thead><tr><th>Tranche</th><th>Taux</th><th>Base</th><th>Impôt</th></tr></thead><tbody>${model.taxRows.map(row => `<tr><td>${escapeHTML(row.label)}</td><td class="amount">${formatMoney(row.rate)} %</td><td class="amount">${formatMoney(row.base)}</td><td class="amount">${formatMoney(row.amount)}</td></tr>`).join('')}</tbody></table></section>` : '';
  const calculation = print.show_calculation_details ? `<section>${model.calculationSteps.length ? `<h2>Détail du calcul enregistré</h2><table><tbody>${model.calculationSteps.map(row => `<tr><td>${escapeHTML(row.label)}</td><td class="amount">${row.amount_text}</td></tr>`).join('')}</tbody></table>` : ''}<p>Coût total employeur enregistré : ${formatMoney(model.employerCharges)}</p></section>` : '';
  return `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8"><title>Bulletin de paie</title><style>
    @page{size:${print.page_size} ${print.orientation};margin:${print.margin}}
    *{box-sizing:border-box}body{margin:0;background:#fff;color:#222;font-family:${cssFont};font-size:${style.fonts.sizes.body}pt;line-height:1.3}
    .document{max-width:${print.orientation === 'landscape' ? '270mm' : '190mm'};margin:auto;padding:${print.margin}}
    header{border-bottom:2px solid ${style.colors.primary};padding-bottom:12px;text-align:${style.logo_position}}
    .logo{max-width:130px;max-height:65px;object-fit:contain}.company-name{font-size:${style.fonts.sizes.header}pt;font-weight:bold;color:${style.colors.primary}}
    .company-details,.small{font-size:${style.fonts.sizes.small}pt}h1{font-size:${style.fonts.sizes.title}pt;color:${style.colors.primary};text-align:center;margin:15px 0 5px}
    h2{font-size:${style.fonts.sizes.header}pt;color:${style.colors.primary}}.period{text-align:center;margin-bottom:14px}
    .employee{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px;background:${style.colors.secondary};padding:12px;margin-bottom:14px}
    .employee div{overflow-wrap:anywhere}.employee strong{display:block;font-size:${style.fonts.sizes.small}pt}.photo{width:60px;height:70px;object-fit:contain}
    table{width:100%;border-collapse:collapse;margin:10px 0;table-layout:fixed}th,td{padding:${template.template_type === 'compact' ? '4px' : '7px'};border:1px solid #d4d4d4;overflow-wrap:anywhere}
    th{background:${style.colors.secondary};font-size:${style.fonts.sizes.small}pt;text-align:left}th:first-child{width:44%}.amount{text-align:right;font-variant-numeric:tabular-nums}
    .section-row td{background:${style.colors.secondary};font-weight:bold}.totals{display:flex;flex-wrap:wrap;border:1px solid ${style.colors.primary};margin-top:14px}
    .total{flex:1;min-width:100px;padding:10px;text-align:right}.total strong{display:block;font-size:${style.fonts.sizes.small}pt}.net{background:${style.colors.secondary};color:${style.colors.accent};font-weight:bold}
    footer{margin-top:20px;display:flex;justify-content:space-between}.signature{min-height:60px}.stamp{border:1px dashed #aaa;padding:20px;font-size:${style.fonts.sizes.small}pt}
    @media(max-width:600px){.document{padding:12px}.employee{grid-template-columns:repeat(2,minmax(0,1fr))}table{font-size:9pt}.totals{display:grid;grid-template-columns:repeat(2,1fr)}}
    @media print{.document{padding:0;max-width:none}}
    ${standard ? `
    body{font-size:10pt}.document{max-width:none;padding:0}
    .standard-heading{display:flex;justify-content:space-between;gap:18px;border-bottom:2px solid ${style.colors.primary};padding-bottom:12px;margin-bottom:14px}
    .standard-heading header{border:0;padding:0;flex:1;min-width:0}.standard-period{text-align:right;flex:1}
    .standard-period h1{text-align:right;font-size:18pt;margin:0 0 4px}.standard-period .period{text-align:right;margin:0}
    .company-name{font-size:16pt;margin-bottom:4px}.company-details,.small{font-size:8pt}
    .employee-heading{background:${style.colors.secondary};color:${style.colors.primary};font-weight:bold;padding:7px 9px;border:1px solid #bccbd8}
    .employee{background:#fff;border:1px solid #bccbd8;border-top:0;padding:9px;gap:7px;margin-bottom:14px}
    .employee strong{font-size:8pt;width:46%;flex-shrink:0}.employee div{font-size:9pt;display:flex;gap:3px;align-items:flex-start}
    .payroll-table th:first-child{width:34%}.payroll-table th:not(:first-child){width:16.5%}
    .payroll-table th,.payroll-table td{padding:5px 7px;font-size:9pt;border:1pt solid #000}.payroll-table th{font-size:8pt}
    tr{break-inside:avoid}thead{display:table-header-group}.section-row{break-after:avoid}
    .settlement{break-inside:avoid;margin-top:14px}.totals{display:grid;grid-template-columns:50.5% 16.5% 16.5% 16.5%;border:1pt solid #000;margin:0}
    .total{min-width:0;padding:0;border-right:1pt solid #000}.total:last-child{border:0}
    .total strong{background:${style.colors.secondary};padding:6px 7px;font-size:8pt;text-align:center;min-height:30px;border-bottom:1pt solid #000}
    .total span{display:block;padding:7px;font-weight:bold;font-size:11pt}.total:first-child{text-align:center}
    .net-banner{display:flex;align-items:center;justify-content:space-between;gap:12px;background:${style.colors.primary};color:#fff;padding:13px 12px;margin-top:10px;font-size:18pt;font-weight:bold}
    footer{margin-top:14px;font-size:8pt}.signature,.stamp{width:31%;min-height:62px;border:0;border-bottom:1px solid #bccbd8;padding:0;font-size:8pt}
    @media screen and (max-width:600px){.document{padding:12px}.standard-heading{flex-wrap:wrap}.totals{grid-template-columns:50.5% 16.5% 16.5% 16.5%}.total strong{font-size:7pt}.total span{font-size:9pt}.net-banner{font-size:15pt}}
    ` : ''}
  </style></head><body><main class="document">${model.isPreview ? '<p class="small">Données de démonstration — aperçu du modèle</p>' : ''}${standard ? '<div class="standard-heading">' : ''}<header>${logo}<div class="company-name">${escapeHTML(model.companyName)}</div>${details}</header>${standard ? '<div class="standard-period">' : ''}
    <h1>BULLETIN DE PAIE</h1><div class="period">${escapeHTML(model.periodName)}<br>${formatDate(period.start_date)} au ${formatDate(period.end_date)}${model.currency ? `<div class="small">Montants en ${model.currency}</div>` : ''}</div>
    ${standard ? '</div></div><div class="employee-heading">IDENTIFICATION DU SALARIÉ</div>' : ''}<section class="employee">${photo}${model.fields.map(([label, value]) => `<div><strong>${escapeHTML(label)}</strong>${escapeHTML(value)}</div>`).join('')}</section>
    <table class="payroll-table"><thead><tr><th>Désignation</th><th>Nombre / taux</th><th>Base</th><th>Retenues</th><th>Gains</th></tr></thead><tbody>
      ${standard ? '' : '<tr class="section-row"><td colspan="5">Gains</td></tr>'}${model.rows.filter(row => row.type === 'gain').map(rowHTML).join('')}
      <tr class="section-row"><td colspan="5">Retenues et cotisations</td></tr>${model.rows.filter(row => row.type === 'deduction').map(rowHTML).join('')}</tbody></table>
    ${standard ? '<div class="settlement">' : ''}<section class="totals">${summary.map(total => `<div class="total ${total.code === 'NET_SALARY' ? 'net' : ''}" data-code="${total.code}"><strong>${escapeHTML(total.label)}</strong><span>${total.amount_text}</span></div>`).join('')}</section>
    ${standard && net ? `<div class="net-banner" data-code="NET_SALARY"><span>${escapeHTML(net.label)}</span><span>${net.amount_text}${model.currency ? ` ${escapeHTML(model.currency)}` : ''}</span></div>` : ''}
    ${model.hiddenCount ? `<p class="small">${model.hiddenCount} rubrique(s) masquée(s) ; les totaux conservent tous les montants enregistrés.</p>` : ''}
    ${tax}${calculation}<footer>${print.show_signature_area ? '<div class="signature">Signature</div>' : ''}${print.show_stamp_area ? '<div class="stamp">Cachet de l’entreprise</div>' : ''}${model.calculationDate ? `<div class="small">Calculé le ${model.calculationDate}</div>` : ''}</footer>
    ${standard ? '</div>' : ''}</main></body></html>`;
}
// Only the built-in standard/professional format adopts this layout. Saved
// company templates retain their configured presentation and summary order.
function isStandardPayslipLayout(template) {
  return template.template_type === 'professional' && !template.id && !template.is_custom;
}
function standardPayslipSummary(model) {
  return ['GROSS_SALARY', 'TAXABLE_BASE', 'TOTAL_DEDUCTIONS', 'TOTAL_GAINS']
    .map(code => model.summary.find(total => total.code === code)).filter(Boolean);
}
function generatePayslipHTML(data, template, settings = {}) {
  return renderPayslipHTML(createPayslipRenderModel(data, template, settings));
}

function createPayslipPreviewData() {
  return {
    employee: { id: 0, employee_number: 'EXEMPLE', first_name: 'Salarié', last_name: 'Exemple', hire_date: '2020-01-01',
      professional_category: 'Catégorie exemple', position_title: 'Poste exemple', department_name: 'Service exemple', dependents_count: 1, payment_mode: 'Virement' },
    company: { name: 'Entreprise de démonstration', address: 'Adresse de démonstration', phone: 'Téléphone de démonstration', email: 'contact@example.invalid' },
    period: { name: 'Septembre 2026 — exemple', start_date: '2026-09-01', end_date: '2026-09-30' },
    earnings: { basicSalary: 200000, seniorityBonus: 10000, additionalSalary: 20000, housingAllowance: 15000,
      transportAllowance: 10000, naturalBenefits: 5000, totalRemuneration: 260000, grossSalary: 260000 },
    deductions: { cnss: { employeeAmount: 14300, employeeRate: 5.5, base: 260000 },
      iuts: { taxableIncome: 230000, iutsNetToPay: 20000, taxDetails: [{ bracket: 'Tranche exemple', rate: 10, taxableAmount: 200000, taxAmount: 20000 }] },
      fsp: { amount: 2000, rate: 1, baseAmount: 200000 }, loans: 5000, advances: 10000, otherDeductions: 3000, totalDeductions: 54300 },
    finalAmounts: { grossSalary: 260000, netSalary: 205700, netToPay: 205700, totalDeductions: 54300, taxableBase: 230000, employerCharges: 290000 },
    calculatedAt: '2026-09-30T12:00:00.000Z',
  };
}

module.exports = { canonicalPayslipElementCode, normalizePayslipElementAliases, normalizePayslipTemplateConfig, createPayslipRenderModel, generatePayslipHTML, renderPayslipHTML, createPayslipPreviewData, formatMoney, formatDate, safeAssetUrl, isStandardPayslipLayout, standardPayslipSummary };
